import { Injectable } from "@angular/core";
import r2Core, {coreCrypto} from "@core/r2";
import { IChatMessage, IChatRepository, IChatSetting } from "@listat/r2-core";
import { WebChatSetting } from "@core/web/web-chat-setting/web-chat-setting";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { firstValueFrom } from "rxjs";
import { SCHEMA_NAME_CHAT_MESSAGE } from "@core/office/office-chat-repository/office-chat-repository";
import {logger} from "@services/logger/logger.service";

export interface IEntryDB {
    id: string;
    version: number;
    history: IHistoryItem;
}

interface IHistoryItem {
    messages: string;
    setting: IChatSetting;
}



@Injectable({
    providedIn: 'root'
})
export class WebChatRepository implements IChatRepository {
    private r2Core = r2Core;

    private defaultSetting: IChatSetting = {
        systemMessage: this.r2Core.constants.chat.SETTING_TONE.FORMAL,
        options: {
            maxGptTokensForEachCall: this.r2Core.constants.chat.SETTING_TOKEN_LENGTH.medium,
        }
    };

    private messages!: IChatMessage[];
    private setting!: IChatSetting;
    private chatSettingSvc!: WebChatSetting;

    private readonly documentID: string = 'web';
    private readonly historyKeySeparator = '--@@--';
    public userID: string | undefined;

    private cryptoService = new coreCrypto.Channel();

    constructor(
        private db: NgxIndexedDBService,
    ) {

    }

    public async initStorage(userID: string | undefined, setting: WebChatSetting): Promise<string> {
        this.cryptoService.ApplySecurityCode(`chat-repository-${userID}`);
        this.userID = userID;
        this.chatSettingSvc = setting;
        await this.initIfEmpty();

        this.chatSettingSvc.$settingUpdate.subscribe((setting) => {
            this.mergeSetting(setting);
        });

        return this.getHistoryKey();
    }

    public getHistoryKey(): string {
        if (!this.userID || !this.documentID) {
            throw new Error('Can not generate history key,please check your value');
        }
        return `${this.userID}${this.historyKeySeparator}${this.documentID}`;
    }

    public async deleteMessages(): Promise<void> {
        const db = await this.getDataFromIDB();
        db.history.messages = await this.cryptoService.EncryptObject([]);
        await firstValueFrom(
            this.db.update<IEntryDB>(SCHEMA_NAME_CHAT_MESSAGE, db)
        );
        this.messages = [];
    }

    public async deleteSetting(): Promise<void> {
        const db = await this.getDataFromIDB();
        this.setting = this.defaultSetting;
        db.history.setting = this.setting;
        await firstValueFrom(
            this.db.update<IEntryDB>(SCHEMA_NAME_CHAT_MESSAGE, db)
        );
    }

    public async mergeSetting(
        setting: IChatSetting,
    ): Promise<IChatSetting | null> {
        this.mergeDeep(this.setting, setting)

        const db = await this.getDataFromIDB();
        db.history.setting = this.setting;

        await firstValueFrom(
            this.db.update<IEntryDB>(SCHEMA_NAME_CHAT_MESSAGE, db)
        );

        this.r2Core.chat.setSettings(this.setting);

        return this.setting;
    }

    public async writeMessages(
        messages: IChatMessage[],
    ): Promise<IChatMessage[] | null> {
        const db = await this.getDataFromIDB();
        const encryptedMessages = await this.cryptoService.EncryptObject(messages);
        db.history.messages = encryptedMessages;
        await firstValueFrom(
            this.db.update<IEntryDB>(SCHEMA_NAME_CHAT_MESSAGE, db)
        );
        this.messages = messages;
        return this.messages;
    }

    public async writeSetting(
        setting: IChatSetting,
    ): Promise<IChatSetting | null> {
        logger.log(setting);
        const db = await this.getDataFromIDB();

        this.setting = setting;
        db.history.setting = this.setting;
        await firstValueFrom(
            this.db.update<IEntryDB>(SCHEMA_NAME_CHAT_MESSAGE, db)
        );

        return db.history.setting;
    }

    public async readMessages(): Promise<IChatMessage[] | null> {
        try {
            const db = await this.getDataFromIDB();
            this.messages = await this.cryptoService.DecryptObject<IChatMessage[]>(db.history.messages);
            return this.messages;
        }
        catch (e) {
            return null;
        }
    }

    public async readSetting(): Promise<IChatSetting | null> {
        const db = await this.getDataFromIDB();
        this.setting = db.history.setting;
        return this.setting;
    }

    private async initIfEmpty(): Promise<void> {
        try {
            await this.getDataFromIDB();
        }
        catch (e) {
            logger.log('Writing to IDB');
            await firstValueFrom(
                this.db.add<IEntryDB>(
                    SCHEMA_NAME_CHAT_MESSAGE,
                    {
                        version: 1,
                        id: this.getHistoryKey(),
                        history: {
                            messages: await this.cryptoService.EncryptObject([]),
                            setting: this.r2Core.chat.getSettings()
                        }
                    }
                )
            );
        }
    }

    private async getDataFromIDB(): Promise<IEntryDB> {
        const key = this.getHistoryKey();
        const data = await firstValueFrom(
            this.db.getByKey<IEntryDB>(SCHEMA_NAME_CHAT_MESSAGE, key)
        );

        if (!data) {
            throw new Error('No data found');
        }

        return data;
    }

    private mergeDeep<T extends Record<string, any>>(target: T, source: T) {
        if (this.isObject(target) && this.isObject(source)) {
            for (const key in source) {
                if (this.isObject(source[key])) {
                    if (!target[key]) Object.assign(target, { [key]: {} });
                    this.mergeDeep(target[key], source[key]);
                } else {
                    Object.assign(target, { [key]: source[key] });
                }
            }
        }
    }

    private isObject(item: any) {
        return (item && typeof item === 'object' && !Array.isArray(item));
    }
}
